import React, { Fragment, useEffect, useState } from "react";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import { StatNurseBookedShifts } from "../components/StatNurseBookedShifts";
import { PageTitleController } from "../components/PageTitleController";
import { BsClipboardDataFill, BsPieChartFill, BsPersonFill, BsPeopleFill, BsCalendarCheck, BsEnvelopeFill } from "react-icons/bs";
import { useAuth } from "../context/AuthContext";
import { StatStaffApprovedShifts } from "../components/StatStaffApprovedShifts";
import { StatConfirmedShiftsPerTrust } from "../components/StatConfirmedShiftsPerTrust";
import { StatEmailLatency } from "../components/StatEmailLatency";
import AxisSpinner from "../components/AxisSpinner";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Card from "react-bootstrap/Card";
import Container from "react-bootstrap/Container";
import Badge from "react-bootstrap/Badge";
import { isAdminRole, isSuperAdminRole } from "../util/Permissions";

export const StatisticsDashboard = () => {
  const { authAxios } = useAuth();
  const [key, setKey] = useState("confirmed"); // Set default tab to confirmed shifts
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState();
  const [summaryData, setSummaryData] = useState({
    openShifts: 0,
    confirmedShifts: 0,
    usersWithAvailability: 0,
    lastUpdated: null
  });

  useEffect(() => {
    document.title = "Statistics";
    fetchCurrentUser();
    fetchSummaryData();
  }, []);

  const fetchCurrentUser = async () => {
    setLoading(true);
    try {
      const config = {
        headers: {
          "content-type": "application/json",
        },
        params: {},
      };

      const response = await authAxios().get(
        "/api/backoffice/users/current_user",
        config,
      );

      setUser(response.data);
    } catch (error) {
      console.log("Error catch");
      console.log(error);
    }
    setLoading(false);
  };
  
  const fetchSummaryData = async () => {
    try {
      const config = {
        headers: {
          "content-type": "application/json",
        }
      };

      // Fetch data from existing endpoints and the users with availability endpoint
      const [openShifts, confirmedShifts, usersWithAvailability] = await Promise.all([
        authAxios().get("/api/backoffice/overview/open_shifts", config),
        authAxios().get("/api/backoffice/overview/confirmed_shifts", config),
        authAxios().get("/api/backoffice/stats/users_with_availability_today", config)
      ]);
      
      setSummaryData({
        openShifts: parseInt(openShifts.data?.value || 0),
        confirmedShifts: parseInt(confirmedShifts.data?.value || 0),
        usersWithAvailability: parseInt(usersWithAvailability.data?.value || 0),
        lastUpdated: new Date()
      });
      
    } catch (error) {
      console.log("Error fetching summary data");
      console.log(error);
    }
  };

  const displayChart = () => {
    if (key === "confirmed") {
      return <StatConfirmedShiftsPerTrust />;
    }
    
    if (key === "nurse") {
      return <StatNurseBookedShifts />;
    }

    if (key === "staff") {
      return <StatStaffApprovedShifts />;
    }

    if (key === "email_latency") {
      return <StatEmailLatency />;
    }

    return null;
  };

  const loadAdminStats = () => {
    if (!isAdminRole(user?.role)) return null;
    return <Tab eventKey="staff" title={<><BsPeopleFill className="me-2" />Staff</>}></Tab>;
  };

  const loadEmailLatencyStats = () => {
    if (!isSuperAdminRole(user?.role)) return null;
    return (
      <Tab
        eventKey="email_latency"
        title={<><BsEnvelopeFill className="me-2" />Email Latency</>}
      ></Tab>
    );
  };
  
  const renderSummaryMetrics = () => {
    return (
      <Row className="mb-4">
        <Col md={4}>
          <Card className="text-center h-100 shadow-sm">
            <Card.Body>
              <h6 className="text-muted mb-2">Open Shifts</h6>
              <h2 className="text-axis-offblack mb-0">{summaryData.openShifts}</h2>
              <Badge bg="warning" className="mt-2">Available</Badge>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="text-center h-100 shadow-sm">
            <Card.Body>
              <h6 className="text-muted mb-2">Users With Availability Today</h6>
              <h2 className="text-axis-offblack mb-0">{summaryData.usersWithAvailability}</h2>
              <Badge bg="info" className="mt-2"><BsCalendarCheck className="me-1" /> Today</Badge>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="text-center h-100 shadow-sm">
            <Card.Body>
              <h6 className="text-muted mb-2">Confirmed Shifts</h6>
              <h2 className="text-axis-offblack mb-0">{summaryData.confirmedShifts}</h2>
              <Badge bg="success" className="mt-2">Active</Badge>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    );
  };

  const spinnerOrData = (loading) => {
    if (loading) return <AxisSpinner loading={loading} />;
    return (
      <Fragment>
        <div className="mb-4">
          {renderSummaryMetrics()}
        </div>
        
        <Card className="shadow-sm mb-4">
          <Card.Body>
            <Tabs
              id="stats-tabs"
              activeKey={key}
              onSelect={(k) => setKey(k)}
              className="mb-3 axis-table-tabs"
            >
              <Tab 
                eventKey="confirmed" 
                title={<><BsPieChartFill className="me-2" />Confirmed Shifts</>}
              ></Tab>
              <Tab 
                eventKey="nurse" 
                title={<><BsPersonFill className="me-2" />Nurse</>}
              ></Tab>
              {loadAdminStats()}
              {loadEmailLatencyStats()}
            </Tabs>
            
            <div className="stats-chart-container p-3">
              {displayChart()}
            </div>
            
            {summaryData.lastUpdated && (
              <div className="text-muted text-end mt-3 small">
                Last updated: {summaryData.lastUpdated.toLocaleString()}
              </div>
            )}
          </Card.Body>
        </Card>
      </Fragment>
    );
  };

  return (
    <Container fluid className="p-3">
      <PageTitleController
        title={"Statistics Dashboard"}
        icon={<BsClipboardDataFill className={"icon-right-spacing"} />}
      />

      {spinnerOrData(loading)}
    </Container>
  );
};
